import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { attendeeAuth, type AttendeeMe } from '../services/attendeeAuth'
import { setOnAuthExpired } from '../services/api'

interface AttendeeAuthState {
  ready: boolean
  /** True when the API/auth is reachable (so the app should gate on sign-in). */
  authAvailable: boolean
  me: AttendeeMe | null
  isAuthenticated: boolean
  refresh: () => Promise<void>
  logout: () => Promise<void>
}

const Ctx = createContext<AttendeeAuthState | undefined>(undefined)

export function AttendeeAuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false)
  const [authAvailable, setAuthAvailable] = useState(false)
  const [me, setMe] = useState<AttendeeMe | null>(null)

  const refresh = useCallback(async () => {
    try {
      const data = await attendeeAuth.me()
      setAuthAvailable(true)
      setMe(data)
    } catch {
      // API/auth unreachable — run in open/demo mode (no gating).
      setAuthAvailable(false)
      setMe(null)
    }
  }, [])

  useEffect(() => {
    let active = true
    const init = async () => {
      await refresh()
      if (active) setReady(true)
    }
    void init()
    return () => {
      active = false
    }
  }, [refresh])

  // When any auth-gated call 401s (expired/rotated session), re-check /attendee/me
  // so the UI flips to a clean signed-out state instead of showing broken pages.
  useEffect(() => {
    setOnAuthExpired(() => {
      void refresh()
    })
    return () => setOnAuthExpired(null)
  }, [refresh])

  const logout = useCallback(async () => {
    try {
      await attendeeAuth.logout()
    } finally {
      await refresh()
    }
  }, [refresh])

  const value = useMemo<AttendeeAuthState>(
    () => ({
      ready,
      authAvailable,
      me,
      isAuthenticated: Boolean(me?.authenticated),
      refresh,
      logout,
    }),
    [ready, authAvailable, me, refresh, logout],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAttendeeAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAttendeeAuth must be used within AttendeeAuthProvider')
  return ctx
}
